import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { UserAvatarComponent } from "../user-avatar/user-avatar.component";
import { KeycloakProfile } from "keycloak-js";
import { KeycloakService } from "keycloak-angular";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { v4 as uuidv4 } from "uuid";
import { firstValueFrom, Subscription } from "rxjs";
import { AuthService } from "../../services/auth/auth.service";
import { AgentApiService } from "../../services/generated/waba-api/services/agent-api.service";
import { CompanyApiService } from "../../services/generated/waba-api/services/company-api.service";
import { EffectiveAccessApiService } from "../../services/generated/waba-api/services/effective-access-api.service";
import { MyAvatarApiService } from "../../services/company/my-avatar-api.service";
import { SidebarIconComponent } from "../../layout/sidebar/sidebar-icon/sidebar-icon.component";
import { SILENT_HTTP_CONTEXT } from "../../shared/utils/silent-http.util";
import {
  buildFixedDropdownStyle,
  isDocumentRtl,
} from "../../shared/utils/rtl-layout.util";

@Component({
  selector: "app-dropdown-user",
  standalone: true,
  imports: [CommonModule, TranslateModule, SidebarIconComponent, UserAvatarComponent],
  templateUrl: "./dropdown-user.component.html",
  styleUrl: "./dropdown-user.component.scss",
})
export class DropdownUserComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("panelRoot") panelRoot?: ElementRef<HTMLElement>;
  @ViewChild("userMenuTrigger") userMenuTrigger?: ElementRef<HTMLButtonElement>;

  panelOpen = false;
  isRTL = false;
  panelStyle: Record<string, string> = {};

  profile: KeycloakProfile | null = null;
  companyId?: string;
  agentPhone = "";
  agentGroupName = "";
  myAvatarUrl?: string;

  private langChangeSub?: Subscription;

  constructor(
    private authService: AuthService,
    private companyService: CompanyApiService,
    private agentService: AgentApiService,
    private effectiveAccessService: EffectiveAccessApiService,
    private keycloakService: KeycloakService,
    private renderer: Renderer2,
    private translate: TranslateService,
    private myAvatarApiService: MyAvatarApiService,
  ) {}

  ngOnInit(): void {
    this.checkDirection();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.checkDirection();
      if (this.panelOpen) {
        this.updatePanelPosition();
      }
    });
    this.loadProfile();
    this.companyId = this.authService.getCompanyId();
    this.myAvatarApiService.getMyAvatarUrl().subscribe({
      next: (url) => (this.myAvatarUrl = url),
      error: () => (this.myAvatarUrl = undefined),
    });
  }

  ngAfterViewInit(): void {
    this.attachPanelToBody();
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
    const root = this.panelRoot?.nativeElement;
    if (root?.parentElement === document.body) {
      this.renderer.removeChild(document.body, root);
    }
  }

  get initials(): string {
    const first = this.profile?.firstName?.charAt(0) || "";
    const last = this.profile?.lastName?.charAt(0) || "";
    const fromName = `${first}${last}`.trim();
    return (fromName || this.profile?.username?.charAt(0) || "U").toUpperCase();
  }

  get displayName(): string {
    const fullName = [this.profile?.firstName, this.profile?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    return fullName || this.profile?.username || "";
  }

  get profileEmail(): string {
    return this.profile?.email?.trim() || "";
  }

  get profilePhone(): string {
    if (this.agentPhone) {
      return this.agentPhone;
    }

    const tokenPhone = this.readPhoneFromToken();
    if (tokenPhone) {
      return tokenPhone;
    }

    const attrs = this.profile?.attributes;
    if (!attrs) {
      return "";
    }

    for (const key of [
      "phoneNumber",
      "phone",
      "mobile",
      "Phone",
      "mobileNumber",
      "phone_number",
    ]) {
      const raw = attrs[key];
      const value = Array.isArray(raw) ? raw[0]?.trim() : "";
      if (value) {
        return value;
      }
    }

    return "";
  }

  displayValue(value: string): string {
    return value || "—";
  }

  async loadProfile(): Promise<void> {
    this.profile = await this.authService.getProfile();
    await Promise.all([this.loadAgentPhone(), this.loadAgentGroup()]);
  }

  private async loadAgentGroup(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.effectiveAccessService.getEffectiveAccess(
          {
            resourceType: "CONTACT",
            "x-api-canal": "web",
            "x-api-requestId": uuidv4(),
          },
          SILENT_HTTP_CONTEXT,
        ),
      );
      this.agentGroupName = response.body?.agentGroupName?.trim() || "";
    } catch {
      this.agentGroupName = "";
    }
  }

  private async loadAgentPhone(): Promise<void> {
    if (!this.authService.getCompanyId()) {
      return;
    }

    try {
      const response = await firstValueFrom(
        this.companyService.getCompanyByCompanyId(
          {
            "x-api-canal": "web",
            "x-api-requestId": uuidv4(),
          },
          SILENT_HTTP_CONTEXT,
        ),
      );
      this.agentPhone = response.body?.currentUserPhoneNumber?.trim() || "";
      if (this.agentPhone) {
        return;
      }
    } catch {
      // Expected for users without company read access (e.g. order-only).
    }

    await this.loadAgentPhoneFromAgentRecord();
  }

  private async loadAgentPhoneFromAgentRecord(): Promise<void> {
    const username = this.profile?.username?.trim();
    const email = this.profile?.email?.trim();
    const authUserId =
      (this.keycloakService.getKeycloakInstance().tokenParsed?.sub as
        | string
        | undefined)?.trim() || "";

    const filters: Array<Record<string, string>> = [];
    if (username) {
      filters.push({ username });
    }
    if (email) {
      filters.push({ email });
    }
    if (authUserId) {
      filters.push({ authUserId });
    }

    for (const filter of filters) {
      try {
        const response = await firstValueFrom(
          this.agentService.searchAgent(
            {
              "x-api-canal": "web",
              "x-api-requestId": uuidv4(),
              body: { page: 0, size: 1, filter: { ...filter, status: true } },
            },
            SILENT_HTTP_CONTEXT,
          ),
        );
        const phone = response.body?.entities?.[0]?.phoneNumber?.trim() || "";
        if (phone) {
          this.agentPhone = phone;
          return;
        }
      } catch {
        // Try next identifier.
      }
    }
  }

  private readPhoneFromToken(): string {
    const parsed = this.keycloakService.getKeycloakInstance()
      .tokenParsed as Record<string, unknown> | undefined;
    if (!parsed) {
      return "";
    }
    for (const key of ["phone_number", "phoneNumber", "phone", "mobile"]) {
      const value = parsed[key];
      if (typeof value === "string" && value.trim()) {
        return value.trim();
      }
    }
    return "";
  }

  openPanel(event?: Event): void {
    event?.stopPropagation();
    this.panelOpen = !this.panelOpen;
    if (this.panelOpen) {
      window.dispatchEvent(new CustomEvent("dashy-user-menu-opened"));
      this.loadProfile();
      setTimeout(() => {
        this.attachPanelToBody();
        this.updatePanelPosition();
      }, 0);
    }
  }

  closePanel(): void {
    this.panelOpen = false;
  }

  private attachPanelToBody(): void {
    const root = this.panelRoot?.nativeElement;
    if (root && root.parentElement !== document.body) {
      this.renderer.appendChild(document.body, root);
    }
  }

  signout(): void {
    this.closePanel();
    this.authService.logout();
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest(".user-panel") && !target.closest(".user-menu-trigger")) {
      this.closePanel();
    }
  }

  @HostListener("document:keydown.escape")
  onEscape(): void {
    this.closePanel();
  }

  @HostListener("window:dashy-header-menu-opened")
  onHeaderMenuOpened(): void {
    this.closePanel();
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    if (this.panelOpen) {
      this.updatePanelPosition();
    }
  }

  @HostListener("window:scroll")
  onWindowScroll(): void {
    if (this.panelOpen) {
      this.updatePanelPosition();
    }
  }

  private updatePanelPosition(): void {
    const trigger =
      this.userMenuTrigger?.nativeElement ||
      document.getElementById("user-menu-button");
    if (!trigger) {
      return;
    }

    this.panelStyle = buildFixedDropdownStyle(trigger, {
      width: 280,
      isRTL: this.isRTL,
    });
  }

  private checkDirection(): void {
    this.isRTL = isDocumentRtl();
  }
}
